import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Container from "@mui/material/Container";
import LayoutCustomer from "./LayoutCustomer";
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Button
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

const OrderConfirmation = () => {
  const intl = useIntl();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError(intl.formatMessage({ id: 'app.orderNotFoundOrError', defaultMessage: 'No se pudo cargar los detalles de la orden.' }));
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LayoutCustomer location={order?order.location:null}>
        <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            <FormattedMessage id="app.loadingOrder" defaultMessage="Cargando detalles de la orden..." />
          </Typography>
        </Container>
      </LayoutCustomer>
    );
  }

  if (error) {
    return (
      <LayoutCustomer location={order?order.location:null}>
        <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="error">{error}</Alert>
          <Button component={Link} to="/" variant="contained" sx={{ mt: 3 }}>
            <FormattedMessage id="app.goHome" defaultMessage="Ir al inicio" />
          </Button>
        </Container>
      </LayoutCustomer>
    );
  }

  if (!order) {
    return (
      <LayoutCustomer location={order?order.location:null}>
        <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="warning">
            <FormattedMessage id="app.noOrderDetails" defaultMessage="No se encontraron detalles para esta orden." />
          </Alert>
          <Button component={Link} to="/" variant="contained" sx={{ mt: 3 }}>
            <FormattedMessage id="app.goHome" defaultMessage="Ir al inicio" />
          </Button>
        </Container>
      </LayoutCustomer>
    );
  }

  return (
    <LayoutCustomer location={order?order.location:null}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'success.main' }}>
              <FormattedMessage id="app.orderConfirmedTitle" defaultMessage="¡Pedido Confirmado!" />
            </Typography>
            <Typography variant="h6" color="text.secondary">
              <FormattedMessage id="app.orderNumber" defaultMessage="Número de Orden: {orderNumber}" values={{ orderNumber: order.orderNumber }} />
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              <FormattedMessage id="app.thankYou" defaultMessage="Gracias por tu pedido. En breve, lo tendrás listo." />
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  <FormattedMessage id="app.orderDetails" defaultMessage="Detalles del Pedido" />
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">
                  <strong><FormattedMessage id="app.location" defaultMessage="Ubicación:" /></strong> {order.location?.name || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong><FormattedMessage id="app.status" defaultMessage="Estado:" /></strong> {order.order_status?.name || order.status}
                </Typography>
                {order.notes && (
                  <Typography variant="body1">
                    <strong><FormattedMessage id="app.generalNotes" defaultMessage="Notas generales:" /></strong> {order.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  <FormattedMessage id="app.orderedItems" defaultMessage="Artículos Pedidos" />
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {order.products && order.products.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body1" fontWeight="bold">
                              {item.order_products.quantity} x {item.name}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              ${(item.order_products.quantity * parseFloat(item.order_products.unitPrice)).toFixed(2)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary">
                              ${parseFloat(item.order_products.unitPrice).toFixed(2)} c/u
                            </Typography>
                            {item.order_products.productNotes && (
                              <Typography component="div" variant="body2" color="text.secondary">
                                <FormattedMessage id="app.itemNotes" defaultMessage="Notas: {notes}" values={{ notes: item.order_products.productNotes }} />
                              </Typography>
                            )}
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">
                    <FormattedMessage id="app.total" defaultMessage="Total:" />
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${parseFloat(order.total).toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: 'center', mt: 3 }}>
            <Button component={Link} to={`/qr/${order.location?.hashId || ''}`} variant="outlined" sx={{ mr: 2 }}>
              <FormattedMessage id="app.backToMenu" defaultMessage="Volver al Menú" />
            </Button>
            {/* Puedes añadir más botones aquí, como "Contactar al staff" o "Ver historial de pedidos" */}
          </Grid>
        </Grid>
      </Container>
    </LayoutCustomer>
  );
};

export default OrderConfirmation;